import "./App.css";
import { useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import * as Tooltip from "@radix-ui/react-tooltip";

/* --------------------------
   Pages — Auth
--------------------------- */
import Home from "@/pages/Home";
// Public pricing reference page (no auth required). Lives at /pricing.
// See note in PricingPage.jsx: this is informational only, NOT a
// checkout flow. Phase 1 stance per pricing-strategy-v2.md.
import PricingPage from "@/pages/PricingPage";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import VerifyEmailRequired from "@/pages/auth/VerifyEmailRequired";
import ActionHandler from "@/pages/auth/ActionHandler";
// Magic-link sign-in (passwordless). Was unrouted pre-May-2026 — the
// MagicLinkLogin component existed but wasn't wired here, so the entire
// flow 404'd. LinkCallback's URL is also referenced by Firebase via
// `actionCodeSettings.url`, so it MUST be registered or the email link
// itself 404s when the user clicks it.
import MagicLinkLogin from "@/pages/auth/MagicLinkLogin";
import LinkCallback from "@/pages/auth/LinkCallback";

/* --------------------------
   Pages — Core App
--------------------------- */
// NOTE: The old coach-side Dashboard.jsx (with checklists, AI Match
// Recap, Club Dashboard, etc.) is no longer mounted — /dashboard now
// renders <Home /> (the universal team picker) for every user. The
// file is preserved in @/pages/Dashboard for historical reference;
// surfacing that information will become its own routes later.
import WeatherAlertResponse from "@/pages/WeatherAlertResponse";
import Notifications from "@/pages/Notifications";
import NotFound from "@/pages/NotFound";
import Unauthorized from "@/pages/Unauthorized";
import PrintNotifications from "@/pages/PrintNotifications";
import MobileUploadPage from "@/pages/MobileUploadPage";
import ChangelogPage from "@/pages/ChangelogPage";

/* --------------------------
   Pages — Teams
--------------------------- */
import TeamsPage from "@/pages/teams/Teams";
import CreateTeams from "@/pages/teams/CreateTeams";
import ViewTeam from "@/pages/teams/ViewTeam";
import ReengagementReview from "@/pages/team/ReengagementReview";
import EditTeam from "@/pages/teams/EditTeam";
import TeamInviteManager from "@/pages/teams/TeamInviteManager";
import TeamSetupWizard from "@/pages/teams/TeamSetupWizard";
// Chat rebuild Phase 0: TeamChatPage replaced by ChatRebuildPlaceholder.
// import TeamChatPage from "@/pages/teams/TeamChatPage";
import AcceptInvite from "@/pages/invite/AcceptInvite";
import ParentTeams from "@/pages/parent/ParentTeams";
import ParentOnboarding from "@/pages/parent/ParentOnboarding";

/* --------------------------
   Pages — Roster
--------------------------- */
import RosterPage from "@/pages/roster/RosterPage";
import TeamScheduleRedirect from "@/pages/teams/TeamScheduleRedirect";

/* --------------------------
   Pages — Messages / AI (Phase 4)
--------------------------- */
import MessagesPage from "@/pages/messages-ai/MessagesPage";
import ComposerWizard from "@/pages/messages-ai/ComposerWizard";

/* --------------------------
   Pages — Schedule
--------------------------- */
import FieldsPage from "@/pages/schedule/FieldsPage";

/* --------------------------
   Pages — Club Admin
--------------------------- */
import ClubDashboard from "@/pages/clubs/ClubDashboard";
import ClubSettings  from "@/pages/clubs/ClubSettings";
import ClubMembers   from "@/pages/clubs/ClubMembers";
import CreateClub    from "@/pages/clubs/CreateClub";
import ClubBroadcast from "@/pages/clubs/ClubBroadcast";
import ClubWebsiteEditor from "@/pages/clubs/ClubWebsiteEditor";

/* --------------------------
   Pages — Programs
--------------------------- */
import ProgramsList                  from "@/pages/programs/ProgramsList";
import CreateProgram                 from "@/pages/programs/CreateProgram";
import EditProgram                   from "@/pages/programs/EditProgram";
import ProgramDetail                 from "@/pages/programs/ProgramDetail";
import ProgramRegistrationsDashboard from "@/pages/programs/ProgramRegistrationsDashboard";
import ProgramRegisterPage           from "@/pages/programs/ProgramRegisterPage";
import ProgramRegisterSuccess        from "@/pages/programs/ProgramRegisterSuccess";

/* --------------------------
   Pages — Tryouts
--------------------------- */
import TryoutsPage from "@/pages/tryouts/TryoutsPage";
import TryoutDetail from "@/pages/tryouts/TryoutDetail";

/* --------------------------
   Pages — Reports
--------------------------- */
import FinancialReports from "@/pages/reports/FinancialReports";

/* --------------------------
   Pages — Seasons & Registration
--------------------------- */
import Seasons from "@/pages/seasons/Seasons";
import CreateSeason from "@/pages/seasons/CreateSeason";
import SeasonDetail from "@/pages/seasons/SeasonDetail";
import RegisterPage from "@/pages/seasons/RegisterPage";
import ParentPortal from "@/pages/ParentPortal";
import RegisterSuccess from "@/pages/seasons/RegisterSuccess";
import DocumentUploadPage from "@/pages/seasons/DocumentUploadPage";
import RSVPPage from "@/pages/seasons/RSVPPage";
import RsvpShortLink from "@/pages/seasons/RsvpShortLink";
import RegistrationsDashboard from "@/pages/seasons/RegistrationsDashboard";
import AttendanceList from "@/pages/seasons/AttendanceList";
import AttendanceSession from "@/pages/seasons/AttendanceSession";
import LineupBuilder from "@/pages/seasons/LineupBuilder";
import SeasonSchedule from "@/pages/seasons/SeasonSchedule";
// Per-team Seasons listing — first-class "all seasons for this team"
// surface, mounted at /teams/:teamId/seasons. New as of May-2026 to
// promote Seasons to a tab in TeamSubNav.
import TeamSeasonsList from "@/pages/teams/TeamSeasonsList";
import ScheduleGateway from "@/pages/seasons/ScheduleGateway";
import EventDetailPage from "@/pages/seasons/EventDetailPage";
import GameRsvpPage from "@/pages/GameRsvpPage";

/* --------------------------
   Pages — Team Chat / Inbox
--------------------------- */
// Chat rebuild Phase 0 (May 2026): old Inbox/ChatRoom/NewChatHandler
// were broken across three competing schemas. Replaced by:
//   - InboxV2 (Phase 2, /messages) — chat list, real
//   - ChatRebuildPlaceholder (Phase 0 fallback for /messages/new and
//     /messages/:chatId until Phase 3 ships the chat detail page)
// import Inbox from "@/pages/messages/Inbox";
// import ChatRoom from "@/pages/messages/ChatRoom";
// import NewChatHandler from "@/pages/messages/NewChatHandler";
import InboxV2 from "@/pages/messages/InboxV2";
import ChatRoomV2 from "@/pages/messages/ChatRoomV2";
import NewDmPage from "@/pages/messages/NewDmPage";
import TeamChatRedirect from "@/pages/messages/TeamChatRedirect";
import ChatRebuildPlaceholder from "@/pages/ChatRebuildPlaceholder";

/* --------------------------
   Pages — Settings
--------------------------- */
import NotificationSettings from "@/settings/NotificationSettings";
import ProfileSettings from "@/settings/ProfileSettings";
import BillingSettings from "@/settings/BillingSettings";

/* --------------------------
   Pages — Billing
--------------------------- */
import Upgrade from "@/pages/billing/Upgrade";

/* --------------------------
   Pages — Match Recap (AI)
--------------------------- */
import RecapPage from "@/pages/recap/RecapPage";

/* --------------------------
   Pages — Support
--------------------------- */
import HelpSupport from "@/pages/support/HelpSupport";

/* --------------------------
   Pages — Admin
--------------------------- */
import AdminLayout from "@/components/AdminLayout";
import AdminRoute from "@/components/AdminRoute";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PlatformClaimsAdmin from "@/pages/admin/PlatformClaimsAdmin";
import ConsentAuditLog from "@/pages/admin/ConsentAuditLog";
import AdminCommunications from "@/pages/admin/AdminCommunications";
import AdminNotifications from "@/pages/admin/AdminNotifications";
// Phase 1 cost-tracking surface — reads collectionGroup('smsUsage') +
// collectionGroup('aiUsage') under platform-only rules. Lives behind
// AdminRoute (founder/support claim required).
import AdminCostDashboard from "@/pages/admin/AdminCostDashboard";
// Beta feedback inbox - reads `feedback` collection under platform-only
// rules. Wired to BetaFeedbackButton (writes) + functions/modules/feedback.js
// (email notifications on new submissions).
import FeedbackAdmin from "@/pages/admin/FeedbackAdmin";
import SeedTeamDemo from "@/pages/admin/SeedTeamDemo";
// Control surface for the Instagram autoposter. Chiefly a kill switch: the
// scheduler publishes publicly with nobody watching, so stopping it should
// not require the Firebase console.
import AdminSocial from "@/pages/admin/AdminSocial";
import AdminRatings from "@/pages/admin/AdminRatings";
import AdminRatingClubs from "@/pages/admin/AdminRatingClubs";
import AdminRatingStats from "@/pages/admin/AdminRatingStats";
// Founder-only club provisioning surface. Replaces the local Admin-SDK
// CLI flow Kurt was running to stamp founding clubs. See
// features/admin-club-provisioning/ for the full spec package.
import AdminClubsList from "@/pages/admin/AdminClubsList";
import AdminClubDetail from "@/pages/admin/AdminClubDetail";
// Founder-only user engagement dashboard. See features/admin-users/.
import AdminUsersList from "@/pages/admin/AdminUsersList";
// Founder-only user detail (click-through from /admin/users).
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
// Founder-only registration contact fixer. See features/admin-registrations/.
import AdminRegistrations from "@/pages/admin/AdminRegistrations";

/* --------------------------
   Pages — Debug
--------------------------- */
import CallableEcho from "@/pages/debug/CallableEcho";
import FCMDebug from "@/pages/debug/FCMDebug";

/* --------------------------
   Components & Context
--------------------------- */
import AppShell from "@/components/Layout/AppShell";
import ScrollToTop from "@/components/ScrollToTop";
import DeepLinkBridge from "@/components/DeepLinkBridge";
import PushNotificationBridge from "@/components/PushNotificationBridge";
import SettingsLayout from "@/components/SettingsLayout";
import PageTransitionLoader from "@/components/PageTransitionLoader";
import RootRedirect from "@/components/routing/RootRedirect";
import VerifiedCoachRoute from "@/components/VerifiedCoachRoute";

import {
  RequireAuth,
  RequireTeamSetupComplete,
  RequireCoachRole,
} from "@/routes/Guards";

import { PlanProvider } from "@/context/PlanContext";
import { PlanSwitchFloating } from "@/components/PlanSwitch";
import { useAuth } from "@/context/AuthContext";
import { useTeam } from "@/context/TeamContext";

import useInboxUnreadCount from "@/hooks/useInboxUnreadCount";
import useSyncUserLanguage from "@/i18n/useSyncUserLanguage";

/* --------------------------
   Helpers
--------------------------- */
function OptionalShell() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <AppShell />;
  return <Outlet />;
}

function TeamInvitesSmartAlias() {
  const { currentTeam } = useTeam();
  return (
    <Navigate
      replace
      to={currentTeam?.id ? `/teams/${currentTeam.id}/invites` : "/teams"}
    />
  );
}

/**
 * Per-route wrappers that key the inner component by :teamId from the
 * URL. Forces a full unmount + remount whenever the user navigates
 * between two teams' routes (e.g. /teams/A/edit → /teams/B/edit) so
 * the inner component starts from a clean slate instead of trying to
 * reconcile stale state + in-flight async loads.
 *
 * Why dedicated wrappers per route instead of one generic wrapper
 * using cloneElement? cloneElement with a new `key` does work in
 * theory, but it depends on the parent recreating the cloned element
 * each render — easy to get wrong and easy to optimize away. A
 * dedicated wrapper that explicitly renders <Component key={id} />
 * is the standard React Router v6 pattern and bulletproof.
 *
 * Without these wrappers a coach editing Team A and then clicking
 * Team B in the sidebar would see Team A's data persist until they
 * hit refresh — the URL changed but the component reused its prior
 * state and any in-flight load from the previous team could win the
 * race and clobber the new team's data.
 */
function KeyedViewTeam() {
  const { teamId } = useParams();
  return <ViewTeam key={teamId || 'no-team'} />;
}
function KeyedEditTeam() {
  const { teamId } = useParams();
  return <EditTeam key={teamId || 'no-team'} />;
}
function KeyedTeamSetupWizard() {
  const { teamId } = useParams();
  return <TeamSetupWizard key={teamId || 'no-team'} />;
}
function KeyedTeamScheduleRedirect() {
  const { teamId } = useParams();
  return <TeamScheduleRedirect key={teamId || 'no-team'} />;
}

/* --------------------------
   App
--------------------------- */
function App() {
  const { firebaseUser, loadingAuth, profileLoading } = useAuth();
  const { currentTeam } = useTeam();

  // Keeps i18next language in sync with the user's profile preference
  useSyncUserLanguage();

  const { count: unreadChatCount } = useInboxUnreadCount(firebaseUser?.uid || null, {
    scope: "user",
    teamId: currentTeam?.id,
  });

  useEffect(() => {
    document.title = unreadChatCount > 0 ? `(${unreadChatCount}) Scrimly` : "Scrimly";
  }, [unreadChatCount]);

  if (loadingAuth || profileLoading) {
    return <PageTransitionLoader />;
  }

  return (
    <div className="min-h-svh bg-neutral-50">
      <PlanProvider>
        <Tooltip.Provider>
          <ScrollToTop />
          <DeepLinkBridge />
          <PushNotificationBridge />

          <Routes>
            {/* ---------- PUBLIC ROUTES ---------- */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* Magic-link sign-in — passwordless flow. The callback URL
                here MUST stay in sync with actionCodeSettings.url in
                MagicLinkLogin.jsx (currently /auth/link-callback) since
                that's the URL Firebase bakes into the email. */}
            <Route path="/auth/magic-link-login" element={<MagicLinkLogin />} />
            <Route path="/auth/link-callback" element={<LinkCallback />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/home" element={<Home />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Invites */}
            <Route path="/invite/:code" element={<AcceptInvite />} />
            <Route path="/i/:code" element={<AcceptInvite />} />
            <Route path="/accept-invite/:code" element={<AcceptInvite />} />

            {/* Public Player Registration (no login required) */}
            <Route path="/register/:teamId/:seasonId" element={<RegisterPage />} />
            <Route path="/register/:teamId/:seasonId/success" element={<RegisterSuccess />} />

            {/* Public Program Registration (no login required) */}
            <Route path="/p/:programId" element={<ProgramRegisterPage />} />
            <Route path="/p/:programId/success" element={<ProgramRegisterSuccess />} />
            <Route path="/upload/:teamId/:registrationId" element={<DocumentUploadPage />} />
            <Route path="/upload/:registrationId" element={<DocumentUploadPage />} />
            <Route path="/rsvp/:sessionId/:registrationId" element={<RSVPPage />} />

            {/* Parent portal — no login required; the 20-char Firestore ID
                acts as the access token (sent in confirmation / receipt
                emails). Sanitized read-only view only. */}
            <Route path="/r/:registrationId" element={<ParentPortal />} />

            {/* Game RSVP — no login required; registrationId is the access token */}
            <Route path="/game-rsvp/:gameId/:registrationId" element={<GameRsvpPage />} />

            {/* Shortened RSVP links for SMS — token lookup in rsvpLinks, then client-side
                redirect to /game-rsvp/:gameId/:registrationId above. */}
            <Route path="/r/:token" element={<RsvpShortLink />} />

            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Legal pages all live on the marketing domain
                (getscrimly.com/privacy, /terms, /sms-policy). See
                src/constants/legal.js for the canonical URLs and
                src/components/LegalLinks.jsx for the shared footer
                component. The marketing repo owns the page content. */}
            <Route path="/welcome" element={<VerifyEmail />} />
            <Route path="/auth/verify-email-required" element={<VerifyEmailRequired />} />
            <Route path="/verify-email-required" element={<Navigate to="/auth/verify-email-required" replace />} />
            <Route path="/mobile-upload/:tokenId" element={<MobileUploadPage />} />
            <Route path="/notifications/print" element={<PrintNotifications />} />
            <Route path="/debug/echo" element={<CallableEcho />} />
            <Route path="/auth/action" element={<ActionHandler />} />

            {/* ---------- PROTECTED ROUTES ---------- */}
            <Route
              element={
                <RequireAuth>
                  <Outlet />
                </RequireAuth>
              }
            >
              {/* Team setup wizard used to live OUTSIDE the AppShell so
                  it ran in a focused full-screen mode. That was the right
                  call when setup was a forced post-onboarding flow, but
                  now that the wizard is also the canonical /teams/create
                  flow, losing the sidebar mid-flow is disorienting (you
                  click Create Team → see sidebar → save Basics → sidebar
                  disappears at the redirect). Moved into the AppShell-
                  wrapped block below so the sidebar stays visible across
                  all wizard steps. */}

              {/* -------- ADMIN ROUTES -------- */}
              <Route
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
                >
                  <Route path="/admin" element={<Navigate replace to="/admin/dashboard" />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/admin/platform-claims" element={<PlatformClaimsAdmin />} />
                  <Route path="/admin/clubs" element={<AdminClubsList />} />
                  <Route path="/admin/clubs/:clubId" element={<AdminClubDetail />} />
                  <Route path="/admin/users" element={<AdminUsersList />} />
                  <Route path="/admin/users/:uid" element={<AdminUserDetail />} />
                  <Route path="/admin/registrations" element={<AdminRegistrations />} />
                  <Route path="/admin/consent-audit" element={<ConsentAuditLog />} />
                  <Route path="/admin/communications" element={<AdminCommunications />} />
                  <Route path="/admin/notifications" element={<AdminNotifications />} />
                  <Route path="/admin/costs" element={<AdminCostDashboard />} />
                  <Route path="/admin/feedback" element={<FeedbackAdmin />} />
                  <Route path="/admin/seed-team-demo" element={<SeedTeamDemo />} />
                  <Route path="/admin/social" element={<AdminSocial />} />
                  <Route path="/admin/ratings" element={<AdminRatings />} />
                  <Route path="/admin/ratings/clubs" element={<AdminRatingClubs />} />
                  <Route path="/admin/ratings/stats" element={<AdminRatingStats />} />
                  <Route path="/debug/fcm" element={<FCMDebug />} />
                </Route>

                {/* -------- MAIN APP SHELL -------- */}
                <Route
                  element={
                    <RequireTeamSetupComplete>
                      <AppShell />
                    </RequireTeamSetupComplete>
                  }
                >
                  {/* ---- Routes accessible to ALL authenticated roles
                       (parents + coaches). Components that need internal
                       role branching handle it themselves (e.g. Dashboard
                       redirects parents → /parent/teams; ViewTeam renders
                       a parent block). ---- */}
                  {/* /dashboard = universal team picker (<Home />).
                      See note at the Dashboard import for context. */}
                  <Route path="/dashboard" element={<Home />} />
                  <Route path="/weather-alerts/:alertId" element={<WeatherAlertResponse />} />
                  <Route path="/changelog" element={<ChangelogPage />} />
                  <Route path="/support" element={<HelpSupport />} />
                  <Route path="/recap" element={<RecapPage />} />
                  <Route path="/schedule" element={<ScheduleGateway />} />
                  <Route path="/seasons/:seasonId/schedule" element={<SeasonSchedule />} />
                  <Route path="/seasons/:seasonId/schedule/:eventId" element={<EventDetailPage />} />
                  {/* Keyed so navigating between two teams' detail pages
                      (e.g. /teams/A → /teams/B) actually re-mounts ViewTeam
                      instead of reusing the previous instance with stale
                      state. See KeyedViewTeam definition above. */}
                  <Route path="/teams/:teamId" element={<KeyedViewTeam />} />
                  {/* Re-engagement Review screen — disengaged parent
                      smart action target. See features/disengaged-parent/. */}
                  <Route path="/teams/:teamId/reengagement" element={<ReengagementReview />} />
                  {/* Smart redirect: looks up the team's auto-created
                      announcement channel and bounces to /messages/:chatId.
                      Lets Team detail / Dashboard / Inbox link to a
                      stable URL without knowing chat ids. */}
                  <Route path="/teams/:teamId/chat" element={<TeamChatRedirect />} />
                  <Route path="/teams/:teamId/roster" element={<RosterPage />} />
                  {/* Smart redirect: looks up the team's most relevant
                      season schedule (open/closed first, any season
                      second) and bounces to /seasons/:id/schedule.
                      Lets the team sub-nav link to a stable team-scoped
                      URL without knowing which season is active. */}
                  <Route path="/teams/:teamId/schedule" element={<KeyedTeamScheduleRedirect />} />
                  {/* /parent/teams used to be the parent-only home.
                      Now redirects to /dashboard — which is the
                      universal team picker for every user (coach,
                      parent, mixed). Kept as a route so deep links
                      from old invite emails still land somewhere
                      sensible. */}
                  <Route path="/parent/teams" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/parent/welcome" element={<ParentOnboarding />} />

                  {/* ---- COACH-ONLY ROUTES ----
                       Wrapped in RequireCoachRole — parents who hit any of
                       these (URL paste, stale link, etc.) get bounced to
                       /parent/teams. Add new coach-only routes inside this
                       block. Real authority lives in Firestore rules + CF
                       role checks; this is UX only. ---- */}
                  <Route element={<RequireCoachRole />}>
                    <Route path="/billing/upgrade" element={<Upgrade />} />

                    {/* Tryouts */}
                    <Route path="/tryouts" element={<TryoutsPage />} />
                    <Route path="/tryouts/:tryoutId" element={<TryoutDetail />} />

                    {/* Seasons & Registration management */}
                    <Route path="/seasons" element={<Seasons />} />
                    {/* Per-team seasons list — destination of the new
                        "Seasons" tab in TeamSubNav. Coach-only because
                        Seasons themselves are coach-managed; parents
                        only ever see one season's schedule via the
                        Schedule tab. */}
                    <Route path="/teams/:teamId/seasons" element={<TeamSeasonsList />} />
                    <Route path="/seasons/create" element={<CreateSeason />} />
                    <Route path="/seasons/:seasonId" element={<SeasonDetail />} />
                    <Route path="/seasons/:seasonId/registrations" element={<RegistrationsDashboard />} />
                    <Route path="/seasons/:seasonId/attendance" element={<AttendanceList />} />
                    <Route path="/seasons/:seasonId/attendance/:sessionId" element={<AttendanceSession />} />
                    <Route path="/seasons/:seasonId/lineup/:sessionId" element={<LineupBuilder />} />

                    {/* Club admin */}
                    <Route path="/clubs/create"              element={<CreateClub />} />
                    <Route path="/clubs/:clubId"             element={<ClubDashboard />} />
                    <Route path="/clubs/:clubId/settings"    element={<ClubSettings />} />
                    <Route path="/clubs/:clubId/website"     element={<ClubWebsiteEditor />} />
                    <Route path="/clubs/:clubId/members"     element={<ClubMembers />} />
                    {/* Legacy /broadcast route now thin-wraps the modern
                        ComposerWizard in club mode (the old standalone
                        ClubBroadcast.jsx page is preserved as a redirect
                        to keep deep links working). New canonical URL:
                        /clubs/:clubId/messages/new */}
                    <Route path="/clubs/:clubId/broadcast"   element={<ClubBroadcast />} />
                    <Route path="/clubs/:clubId/messages/new" element={<ComposerWizard />} />

                    {/* Programs */}
                    <Route path="/clubs/:clubId/programs"                                        element={<ProgramsList />} />
                    <Route path="/clubs/:clubId/programs/create"                                 element={<CreateProgram />} />
                    <Route path="/clubs/:clubId/programs/:programId"                             element={<ProgramDetail />} />
                    <Route path="/clubs/:clubId/programs/:programId/edit"                        element={<EditProgram />} />
                    <Route path="/clubs/:clubId/programs/:programId/registrations"               element={<ProgramRegistrationsDashboard />} />

                    {/* Team management (coach-side) */}
                    <Route path="/teams" element={<TeamsPage />} />
                    {/* Team creation now uses TeamSetupWizard (same component
                        mounted at /teams/:teamId/setup just below). The wizard
                        detects "new team" mode by the absence of :teamId in the
                        URL, shows only the Basics card, and on first save creates
                        the team + redirects to /teams/{newId}/setup so the rest
                        of the wizard becomes reachable. CreateTeams.jsx is no
                        longer mounted but kept in the repo as historical
                        reference. */}
                    <Route path="/teams/create" element={<TeamSetupWizard />} />
                    {/* React Router v6 reuses the same component instance across
                        navigations within the same route pattern. So if a coach
                        is on /teams/A/edit and clicks team B in the sidebar (URL
                        becomes /teams/B/edit), React doesn't unmount EditTeam —
                        it just re-renders with new params. The useEffect inside
                        EditTeam re-fires on teamId change, but multiple in-flight
                        async loads + carry-over state (errors, processedLogo,
                        teamFields) caused stale data to linger until refresh.
                        Forcing a full remount via key={teamId} is the simplest
                        bulletproof fix — fresh state + fresh effects every time. */}
                    <Route path="/teams/:teamId/setup" element={<KeyedTeamSetupWizard />} />
                    <Route path="/teams/:teamId/edit" element={<KeyedEditTeam />} />
                    <Route path="/teams/:teamId/invites" element={<TeamInviteManager />} />
                    <Route path="/teams/:teamId/fields" element={<FieldsPage />} />
                    <Route path="/teams/invite-links" element={<TeamInvitesSmartAlias />} />

                    {/* Reports */}
                    <Route path="/reports/financial" element={<FinancialReports />} />

                    {/* AI Messages (Phase 4) — coach broadcast tool */}
                    <Route path="/teams/:teamId/messages" element={<MessagesPage />} />
                    <Route path="/teams/:teamId/messages/new" element={<ComposerWizard />} />
                  </Route>

                  {/* ---- Team Chat / Inbox ----
                      Parents are real channel members (Stream enforces this
                      server-side), so they need access too. We allow the full
                      member-set for chat, but keep /notifications coach-only
                      since parents don't manage broadcast preferences. */}
                  <Route
                    element={
                      <VerifiedCoachRoute
                        requireVerified={false}
                        allowedRoles={['coach', 'manager', 'team_manager', 'owner', 'parent', 'player']}
                      />
                    }
                  >
                    {/* Chat v2 routes (rebuild Phases 2-6, May 2026):
                        - Phase 2: /messages (inbox)
                        - Phase 3: /messages/:chatId (thread, read-only at the time)
                        - Phase 4: composer in ChatRoomV2 (send text)
                        - Phase 5: image attachments in composer
                        - Phase 6: /messages/new (DM picker — start 1:1) */}
                    <Route path="/messages" element={<InboxV2 />} />
                    <Route path="/messages/new" element={<NewDmPage />} />
                    <Route path="/messages/:chatId" element={<ChatRoomV2 />} />
                  </Route>
                  <Route element={<VerifiedCoachRoute requireVerified />}>
                    <Route path="/notifications" element={<Notifications />} />
                  </Route>

                  {/* ---- Settings ---- */}
                  <Route path="/settings" element={<SettingsLayout />}>
                    <Route index element={<Navigate replace to="/settings/profile" />} />
                    <Route path="profile" element={<ProfileSettings />} />
                    <Route path="notifications" element={<NotificationSettings />} />
                    <Route path="billing" element={<BillingSettings />} />
                  </Route>
                </Route>
              </Route>

            {/* Fallback */}
            <Route path="*" element={<NotFound />} />

          </Routes>

          <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
          <PlanSwitchFloating />
        </Tooltip.Provider>
      </PlanProvider>
    </div>
  );
}

export default App;
